import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link, useSearchParams } from "react-router-dom";
import { Grid } from "@mui/system";
import UnifierSettingsCodeConsole from "./UnifierSettingsCodeConsole";
import UnifierSettingsTermTable from "./UnifierSettingsTermTable";
import { useSelector } from "react-redux";
import { useMemo } from "react";
import BrandOverviewTableSkeleton from "../../../BrandSkeletons/BrandOverviewTableSkeleton";

const tableHeadings = {
  source_field_expression: "Source Field Expression",
  destination_field_name: "Destination Field Name",
};

const UnifierSettings = () => {
  const [searchParams] = useSearchParams("");
  const loaderForSwitchingTabs = useSelector(
    (state: any) => state.brandConfigurationReport.loaderForSwitchingTabs,
  );
  // Dynamically reads which tab is active from URL
  const reportType = searchParams.get("report") || "daily_user";
  const brandOverviewUnifierSettingsData = useSelector(
    (state: any) =>
      state.brandConfigurationReport.brandOverviewUnifierSettingsData[
        reportType
      ],
  );

  const accordionUnifierSettingsData = useMemo(() => {
    if (
      !brandOverviewUnifierSettingsData ||
      brandOverviewUnifierSettingsData.length === 0
    )
      return [];

    // Group data by raw_table_id and take calculation_type & report_name from first item in group
    const grouped = (brandOverviewUnifierSettingsData as any[]).reduce(
      (acc: Record<number, any>, curr: any) => {
        const key = curr.raw_table_id;
        if (!acc[key]) {
          acc[key] = {
            title: `Raw Table ID:`,
            linkValue: key,
            // Take calculation_type & report_name from first item in group
            calculation_type: curr.calculation_type,
            report_name: curr.report_name,
            report_id: curr.report_id,
            otherDatas: [],
          };
        }
        acc[key].otherDatas.push(curr);
        return acc;
      },
      {},
    );

    return Object.values(grouped);
  }, [brandOverviewUnifierSettingsData]);

  return (
    <>
      <Typography px={2} py={1} variant="h6">
        Unifier Settings
      </Typography>
      {loaderForSwitchingTabs && <BrandOverviewTableSkeleton />}

      {accordionUnifierSettingsData?.length > 0
        ? accordionUnifierSettingsData?.map((item: any, index: number) => (
            <Accordion
              key={index}
              defaultExpanded={index === 0}
              disableGutters
              sx={{
                boxShadow: "none",
                border: "1px solid #0000001F",
                borderRadius: 0,
                m: 0,
                "&.Mui-expanded": { m: 0 },
                "&:before": { display: "none" },
                "& + .MuiAccordion-root": {
                  mt: 0,
                },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  {item.title}
                  &nbsp;
                  <Link
                    style={{ color: "#9C27B0" }}
                    to="#"
                    onClick={(e) => e.preventDefault()} // stops URL change
                  >
                    {item.linkValue}
                  </Link>
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Grid container spacing={3}>
                  <Grid size={6}>
                    <Grid container spacing={2} mb={1}>
                      <Grid size={5}>
                        <Paper
                          sx={{
                            p: 1,
                            boxShadow: "none",
                            border: "1px solid #0000001F",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "12px",
                              color: "#00000099",
                              mb: 1.3,
                            }}
                          >
                            Calculation Type
                          </Typography>
                          <Typography variant="body2">
                            {item.calculation_type ?? ""}
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid size={7}>
                        <Paper
                          sx={{
                            p: 1,
                            boxShadow: "none",
                            border: "1px solid #0000001F",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "12px",
                              color: "#00000099",
                              mb: 1.3,
                            }}
                          >
                            Report Name
                          </Typography>
                          <Typography variant="body2">
                            {item.report_name ?? ""}{" "}
                            {item.report_id ? `(ID: ${item.report_id})` : ""}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    <UnifierSettingsTermTable
                      tableHeadings={tableHeadings}
                      rows={item.otherDatas}
                    />
                  </Grid>

                  <Grid size={6}>
                    <UnifierSettingsCodeConsole items={item.otherDatas} />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))
        : !loaderForSwitchingTabs && (
            <Typography px={2} py={2} variant="body2" color="text.secondary">
              No unifier settings found.
            </Typography>
          )}
    </>
  );
};

export default UnifierSettings;
